import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { Button, Grid, MenuItem, TextField } from "@mui/material";
import CurrentContract from "../../component/Contract/CurrentContract";
import NewContractForm from "../../component/Contract/NewContractForm";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import {
  axiosAPIWithJson,
  axiosGet,
  axiosPostWithJson,
} from "../../util/axios";
import { initContractListThunk } from "../../redux/staticInfo/thunk";
import {
  OpenSnackBarWithResultFail,
  OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";
import { ButtonTextGrid } from "../..";
import FormDialog from "../../component/common/FormDialog";
import { ContractList } from "../../redux/staticInfo/state";
import {
  clearContractDataAction,
  handleInitEditContractAction,
  handleOpenAddZoneDialogAction,
  handleCloseAddZoneDialogAction,
} from "../../redux/contract/action";
import { ContractInfo } from "../../redux/contract/state";
import AddIcon from "@mui/icons-material/Add";

const MAX_IMAGE_TYPE_SELECTION = 5;

function Contract() {
  const dataFetchedRef = useRef(false);
  const dispatch = useDispatch<IRootThunkDispatch>();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const { Contract, openAddZoneDialog } = useSelector((state: IRootState) => state.contractState);
  const { contractList } = useSelector((state: IRootState) => state.staticInfoState);

  // Add zone form state
  const [addZoneContractId, setAddZoneContractId] = useState<string>("");
  const [addZoneName, setAddZoneName] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    getContractData();
    return () => {
      controller.abort();
    };
  }, []);

  async function getContractData() {
    dispatch(OpenLoadingBarThunk(true));
    const contract = await axiosGet("/api/contract");
    if (contract.data.success) {
      dispatch(OpenLoadingBarThunk(false));
      dispatch(initContractListThunk(contract.data.contractList));
    } else {
      dispatch(OpenLoadingBarThunk(false));
      dispatch(OpenSnackBarWithResultFail("無法取得合約表"));
    }
  }

  function handleClickAddContractBtn() {
    setOpenAddDialog(true);
  }

  function handleClickAddZoneBtn() {
    setAddZoneContractId("");
    setAddZoneName("");
    dispatch(handleOpenAddZoneDialogAction());
  }

  function handleCloseDialog() {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    dispatch(clearContractDataAction());
  }

  function handleCloseAddZoneDialog() {
    dispatch(handleCloseAddZoneDialogAction());
    setAddZoneContractId("");
    setAddZoneName("");
  }

  function validateContract(Contract: ContractInfo): string | null {
    if (!Contract.contract) {
      return "你未有填寫合約名稱";
    }
    const imageTypeList = Contract.imageTypeList || [];
    if (imageTypeList.length > MAX_IMAGE_TYPE_SELECTION) {
      return `每份合約最多只可新增 ${MAX_IMAGE_TYPE_SELECTION} 種相片類型`;
    }
    for (let i = 0; i < imageTypeList.length; i++) {
      if (!imageTypeList[i].image_type_name.trim()) {
        return `第 ${i + 1} 個相片類型的中文名稱不能為空`;
      }
      if (!imageTypeList[i].image_type_name_en.trim()) {
        return `第 ${i + 1} 個相片類型的英文名稱不能為空`;
      }
      if (!imageTypeList[i].report_column_name.trim()) {
        return `第 ${i + 1} 個相片類型的報告欄位名稱不能為空`;
      }
    }
    return null;
  }

  async function handleSubmitNewContract() {
    const error = validateContract(Contract);
    if (error) {
      dispatch(OpenSnackBarWithResultFail(error));
      return;
    }

    dispatch(OpenLoadingBarThunk(true));
    const payload = {
      contract: Contract.contract,
      report_title: Contract.report_title || '',
      is_active: Contract.is_active,
      need_inspect: Contract.need_inspect ?? '1',
      imageTypeList: (Contract.imageTypeList || []).map(t => ({
        image_type_name: t.image_type_name,
        image_type_name_en: t.image_type_name_en,
        report_column_name: t.report_column_name,
        is_active: t.is_active,
      })),
      zoneList: Contract.zoneList || [],
    };
    const contract = await axiosPostWithJson("/api/contract", payload);
    if (contract.data.success) {
      dispatch(OpenSnackBarWithResultSuccess(contract.data.message));
      getContractData();
      handleCloseDialog();
      dispatch(OpenLoadingBarThunk(false));
    } else {
      dispatch(OpenSnackBarWithResultFail(contract.data.message));
    }
  }

  function handleClickEditButton(contract: ContractList) {
    setOpenEditDialog(true);
    dispatch(handleInitEditContractAction(contract));
  }

  async function handleSubmitEditContract() {
    const error = validateContract(Contract);
    if (error) {
      dispatch(OpenSnackBarWithResultFail(error));
      return;
    }

    dispatch(OpenLoadingBarThunk(true));
    const payload = {
      contract_id: Contract.contract_id,
      contract: Contract.contract,
      report_title: Contract.report_title || '',
      is_active: Contract.is_active,
      need_inspect: Contract.need_inspect ?? '1',
      imageTypeList: (Contract.imageTypeList || []).map(t => ({
        cleaning_image_type_id: t.cleaning_image_type_id,
        image_type_name: t.image_type_name,
        image_type_name_en: t.image_type_name_en,
        report_column_name: t.report_column_name,
        is_active: t.is_active,
      })),
      zoneList: Contract.zoneList || [],
    };
    const contract = await axiosAPIWithJson.put(`/api/contract`, payload);
    if (contract.data.success) {
      dispatch(OpenSnackBarWithResultSuccess(contract.data.message));
      getContractData();
      handleCloseDialog();
      dispatch(OpenLoadingBarThunk(false));
    } else {
      dispatch(OpenSnackBarWithResultFail(contract.data.message));
    }
  }

  async function handleSubmitNewZone() {
    if (!addZoneContractId) {
      dispatch(OpenSnackBarWithResultFail("請選擇合約"));
      return;
    }
    if (!addZoneName.trim()) {
      dispatch(OpenSnackBarWithResultFail("請輸入區份名稱"));
      return;
    }

    dispatch(OpenLoadingBarThunk(true));
    try {
      const response = await axiosPostWithJson("/api/contract/zone", {
        contract_id: addZoneContractId,
        zone_name: addZoneName.trim(),
      });
      if (response.data.success) {
        dispatch(OpenSnackBarWithResultSuccess(response.data.message));
        handleCloseAddZoneDialog();
        dispatch(OpenLoadingBarThunk(false));
      } else {
        dispatch(OpenSnackBarWithResultFail(response.data.message));
      }
    } catch (error) {
      dispatch(OpenSnackBarWithResultFail("新增區份失敗"));
    }
  }

  return (
    <Grid style={{ display: "flex", flexDirection: "column", gap: 30 }}>
      <Grid style={{ display: "flex", gap: 12 }}>
        <Button variant="contained" onClick={handleClickAddContractBtn}>
          <ButtonTextGrid>新增合約</ButtonTextGrid>
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={handleClickAddZoneBtn}
        >
          <ButtonTextGrid>新增區份</ButtonTextGrid>
        </Button>
      </Grid>
      <CurrentContract
        openEditDialog={openEditDialog}
        handleClickEditButton={handleClickEditButton}
      />

      <FormDialog
        title="新增合約"
        open={openAddDialog}
        handleClose={handleCloseDialog}
        size="lg"
        handleSubmit={handleSubmitNewContract}
      >
        <NewContractForm />
      </FormDialog>

      <FormDialog
        title="更改合約"
        open={openEditDialog}
        handleClose={handleCloseDialog}
        size="lg"
        handleSubmit={handleSubmitEditContract}
      >
        <NewContractForm />
      </FormDialog>

      <FormDialog
        title="新增區份"
        open={openAddZoneDialog}
        handleClose={handleCloseAddZoneDialog}
        size="sm"
        handleSubmit={handleSubmitNewZone}
      >
        <Grid container spacing={2} style={{ paddingTop: 8 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="選擇合約"
              value={addZoneContractId}
              onChange={(e) => setAddZoneContractId(e.target.value)}
              required
            >
              {(contractList || []).map((c) => (
                <MenuItem key={c.contract_id} value={c.contract_id}>
                  {c.contract}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="區份名稱"
              value={addZoneName}
              onChange={(e) => setAddZoneName(e.target.value)}
              placeholder="請輸入區份名稱"
              required
            />
          </Grid>
        </Grid>
      </FormDialog>
    </Grid>
  );
}

export default Contract;
